import { useState, useEffect, useMemo } from "react";
import {
  Col,
  Progress,
  Button,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { GoArrowLeft } from "react-icons/go";
import { FormattedMessage, useIntl } from "react-intl";
import StudentModule from "../../components/Student/StudentModule";
import jump from "jump.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StudentGy25FinalExamModule from "../../components/Student/StudentGy25FinalExamModule";
import axios from "../../services/api/api";
import { getWinOptions, composeUrl } from "../../Utilities/moduleHelper";
import { toast } from "react-toastify";
import { GetStudentCourses } from "../../actions/studentQueries";
import { useQueryClient } from "@tanstack/react-query";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_LOGIN_REDIRECT_URL_NO_LWC } from "../../constants/api.constants";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { buildDownloadFileName } from "../../Utilities/fileHelper";

export default function StudentCourseModules(props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { courseId } = useParams();
  const studentCourses = GetStudentCourses();
  const queryClient = useQueryClient();
  const [showButton, setShowButton] = useState(false);
  const [scormIsOpen, setScormIsOpen] = useState(false);
  const [docDropDownOpen, setDocDropDownOpen] = useState(false);

  const course = studentCourses.data?.find((c) => c.id === Number(courseId));
  let timer = null;
  let scormWindow = null;

  const filters = useMemo(
    () => ({
      all: "all",
      notStarted: "notStarted",
      InProgress: "InProgress",
      moduleDone: "moduleDone",
      approved: "approved",
    }),
    [],
  );

  useEffect(() => {
    const handleScrollButton = () => {
      setShowButton(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScrollButton);
    return () => window.removeEventListener("scroll", handleScrollButton);
  }, []);

  const scrollToTop = () => {
    jump("body", { duration: 600 });
  };

  const filterItems = [
    {
      text: <FormattedMessage id="AllModules" />,
      filter: filters.all,
    },
    {
      text: <FormattedMessage id="NotStarted" />,
      filter: filters.notStarted,
    },
    {
      text: <FormattedMessage id="InProgress" />,
      filter: filters.InProgress,
    },
    {
      text: <FormattedMessage id="ModuleDone" />,
      filter: filters.moduleDone,
    },
    {
      text: <FormattedMessage id="ApprovedPlural" />,
      filter: filters.approved,
    },
  ];

  const [selectedFilter, setSelectedFilter] = useState(filters.all);
  const [filteredModules, setFilteredModules] = useState(
    course ? course.modules.sort((a, b) => a.sortOrder - b.sortOrder) : [],
  );

  const sortModules = (modules) =>
    [...modules].sort((a, b) => a.sortOrder - b.sortOrder);

  const filterModulesByStatus = (modules, status) => {
    return modules
      .map((m) => ({
        ...m,
        finalExamVariants:
          m.finalExamVariants?.filter((f) => f.status === status) ?? [],
      }))
      .filter(
        (m) =>
          m.status === status ||
          m.finalExamVariants?.some((f) => f.status === status),
      );
  };

  useEffect(() => {
    if (!course) return;
    const { modules } = course;

    const getFilteredModules = () => {
      switch (selectedFilter) {
        case filters.notStarted:
          return filterModulesByStatus(modules, "NotStarted");
        case filters.InProgress:
          return filterModulesByStatus(modules, "InProgress");
        case filters.moduleDone:
          return filterModulesByStatus(modules, "Done");
        case filters.approved:
          return filterModulesByStatus(modules, "Completed");
        default:
          return modules;
      }
    };

    setFilteredModules(sortModules(getFilteredModules()));
  }, [selectedFilter, course, filters]);

  const showError = (errorMsg, closeAfter) => {
    toast.error(<FormattedMessage id={errorMsg} />, TOAST_CONFIG);
    return;
  };

  const exitWindow = (studentResultId) => {
    if (scormWindow.closed) {
      clearInterval(timer);
      if (studentResultId === null) {
        showError("Tostify.Message.NoStudentResultToReportScormOn", 2000);
      }
      queryClient.invalidateQueries({ queryKey: ["FetchStudentCourses"] });
      setScormIsOpen(false);
    }
  };

  const calcPercentage = (max, val) => {
    let retVal = 0;
    if (typeof max === "number" && typeof val === "number" && val > 0) {
      retVal = Math.round((val / max) * 100);
    }
    return retVal;
  };

  const runModule = (event, moduleId, moduleType, moduleUrl, variantId) => {
    event.preventDefault();
    if (scormIsOpen) {
      showError("Tostify.Message.RunningModule", 5000);
      return;
    }
    setScormIsOpen(true);
    let runModuleUrl =
      "/student/RunModule?courseId=" + course.id + "&moduleId=" + moduleId;
    if (variantId) {
      runModuleUrl = runModuleUrl + "&variantId=" + variantId;
    }
    const isGy25FinalExam = variantId != null;
    axios
      .post(runModuleUrl)
      .then((data) => {
        if (data?.data?.result) {
          openModule(moduleType, moduleUrl, data.data.result, isGy25FinalExam);
        } else {
          toast.error(<FormattedMessage id="GenericApiError" />, TOAST_CONFIG);
        }
      })
      .catch((err) => {
        if (err?.response?.data === "ModuleIsLocked \n") {
          queryClient.invalidateQueries({ queryKey: ["FetchStudentCourses"] });
          toast.error(
            <FormattedMessage id="YourTeacherLockedThisModuleError" />,
            TOAST_CONFIG,
          );
        } else {
          toast.warn(err?.response?.data);
        }
        setScormIsOpen(false);
      });
  };

  const openModule = (
    moduleType,
    moduleUrl,
    studentResultData,
    isGy25FinalExam,
  ) => {
    if (studentResultData.id === undefined) {
      return;
    }
    if (moduleUrl == null) {
      showError("Tostify.Message.ModuleUrlMissing", 2000);
      return;
    }
    var url = composeUrl(
      studentResultData.student.id,
      studentResultData.courseId,
      studentResultData.moduleId,
      moduleType,
      studentResultData.id,
      moduleUrl,
      studentResultData.student.name,
      studentResultData.numberOfRuns,
      isGy25FinalExam,
    );
    var winOptions = getWinOptions();
    scormWindow = window.open(url, "", winOptions);
    if (scormWindow) {
      scormWindow.location.href = url;
      timer = setInterval(function () {
        exitWindow(studentResultData.id);
      }, 500);
    }
    scormWindow.moveTo(0, 0);
    scormWindow.focus();
  };

  const renderFilters = () => {
    return (
      <div className="filter-section">
        {filterItems.map((filter, i) => (
          <div
            key={filter.filter}
            className={`filter-item ${
              selectedFilter === filter.filter ? "selected" : ""
            }`}
            onClick={() => setSelectedFilter(filter.filter)}
          >
            {filter.text}
            {i < filterItems.length - 1 && <span className="filter-divider" />}
          </div>
        ))}
      </div>
    );
  };

  const documentItems = (doc) => (
    <DropdownItem
      key={doc.id}
      tag="a"
      href={doc.fileUrl || doc.value}
      className="text-truncate text-primary"
      title={doc.value}
    >
      {doc.value}
    </DropdownItem>
  );

  if (studentCourses.isPending) return <Pacmanloader />;
  if (!course) return null;

  const studentGy25FinalExamModules = filteredModules.filter(
    (mod) => mod.finalExamVariants?.length > 0 && mod.isGy25,
  );

  const otherModules = filteredModules.filter((mod) => !mod.isGy25FinalExam);

  const getModuleUploads = (mod) =>
    course.courseUploads.filter(
      (cu) =>
        cu.forTheseModules.includes(mod.id) ||
        (mod.fmId != null && cu.forTheseModules.includes(mod.fmId)),
    );

  return (
    <div className="student-course-modules-container">
      <Col lg="8" className="p-4 align-self-start">
        <div className="ms-4">
          <div className="text-start mb-2">
            <span
              className="d-inline-flex align-items-center go-back"
              onClick={() => navigate(-1)}
            >
              <GoArrowLeft className="go-arrow-left" size={20} />
              <FormattedMessage id="GoBack" />
            </span>
          </div>
          <h1 className="display-5 student-course-name">{course.name}</h1>
          <hr className="my-1 ms-0 break-line" />
          <p>{course.description}</p>
          {course.glossaryFileUrl ? (
            <div className="mb-3 d-flex align-items-center">
              <MenuBookIcon className="me-1 fs-5 text-primary" />
              <a
                href={`${BASE_LOGIN_REDIRECT_URL_NO_LWC}/${course.glossaryFileUrl}`}
                download={buildDownloadFileName(
                  intl.formatMessage({ id: "Glossary" }),
                  course.name || intl.formatMessage({ id: "Course" }),
                  course.glossaryFileUrl,
                )}
              >
                <FormattedMessage id="Glossary" />
              </a>
              <span className="ms-2 text-muted">
                - <FormattedMessage id="GlossaryDescription" />
              </span>
            </div>
          ) : (
            ""
          )}
          {course.courseUploads.filter((cu) => cu.forTheseModules.length < 1)
            .length > 0 && (
            <div className="col-3 mb-3">
              <Dropdown
                isOpen={docDropDownOpen}
                toggle={() => setDocDropDownOpen(!docDropDownOpen)}
              >
                <DropdownToggle
                  color="secondary"
                  className="doc-dropdown-toggle"
                  outline
                  caret
                >
                  <FormattedMessage id="Documents" />
                </DropdownToggle>
                <DropdownMenu className="w-100">
                  {course.courseUploads
                    .filter((cu) => cu.forTheseModules.length < 1)
                    .map(documentItems)}
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
          <div className="d-flex align-items-center">
            <span className="me-2 fw-semibold">
              <FormattedMessage id="Completed/Settled" />:
            </span>
            <div className="progress-block">
              <Progress
                className="progress-course"
                value={course.progressPercentage}
                max={100}
              ></Progress>
            </div>

            <span className="ms-1 fw-semibold">
              {course.progressPercentage}%
            </span>
          </div>
        </div>
        <div className="mt-4 ms-4 d-flex flex-column">
          <Col className="p-4 student-module-col">
            <h2 className="student-module-subheading">
              <FormattedMessage id="Modules" />
            </h2>
            {renderFilters()}
          </Col>

          {filteredModules.length > 0 ? (
            <>
              {otherModules && otherModules.length > 0 && (
                <Col className="student-module-section modules-border">
                  {otherModules.map((mod) => (
                    <StudentModule
                      module={mod}
                      courseUploads={getModuleUploads(mod)}
                      key={mod.id}
                      disabled={
                        (mod.isFinalExam && !course.finalExamEnabled) ||
                        props.disableOpen
                      }
                      moduleIsLocked={mod.isLocked}
                      licenseExpired={course.disabled}
                      courseId={course.id}
                      exitWindow={exitWindow}
                      calcPercentage={calcPercentage}
                      runModule={runModule}
                    />
                  ))}
                </Col>
              )}
              {studentGy25FinalExamModules &&
                studentGy25FinalExamModules.length > 0 && (
                  <Col className="student-module-section exam-border">
                    {studentGy25FinalExamModules.map((mod) => (
                      <StudentGy25FinalExamModule
                        exam={mod}
                        moduleIsLocked={mod.isLocked}
                        disabled={!course.finalExamEnabled || props.disableOpen}
                        licenseExpired={course.disabled}
                        courseId={course.id}
                        courseUploads={getModuleUploads(mod)}
                        exitWindow={exitWindow}
                        calcPercentage={calcPercentage}
                        runModule={runModule}
                      />
                    ))}
                  </Col>
                )}
            </>
          ) : (
            <Card className="p-2 no-modules-match">
              <CardBody>
                <h5 className="lead">
                  <FormattedMessage id="NoModulesMathingFilterChooseAnotherToSeeModules" />
                </h5>
              </CardBody>
            </Card>
          )}
        </div>
      </Col>

      {showButton && (
        <Button onClick={scrollToTop} className="backToTopButton">
          <FontAwesomeIcon icon="arrow-up" size="lg" color="#fff" />
        </Button>
      )}
    </div>
  );
}
